import React from 'react';
import { Link } from 'react-router-dom';

import titleImg from '../../assets/img/logo-lockup.svg';

// Self-contained privacy policy. Does NOT use the retired marketing
// Header/NavFooter (those link to removed routes). Reachable at /privacy
// without authentication so it can be linked from the App Store / Play
// Console listings. Written to modern standards and to what the app
// actually does — notably, pronunciation scoring runs on-device, so voice
// recordings never reach our servers.

const LAST_UPDATED = 'June 5, 2026';

const FONT_BODY = '"Hanken Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const FONT_DISPLAY = '"Fredoka", system-ui, sans-serif';

const styles = {
	page: {
		fontFamily: FONT_BODY,
		color: '#352F27',
		lineHeight: 1.6,
		backgroundColor: '#FBF7F0',
		minHeight: '100vh',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		maxWidth: 760,
		margin: '0 auto',
		padding: '24px 24px 0',
	},
	logo: { height: 34 },
	back: { color: '#D86641', fontWeight: 700, textDecoration: 'none' },
	container: { maxWidth: 760, margin: '0 auto', padding: '8px 24px 64px' },
	h1: { fontFamily: FONT_DISPLAY, fontSize: 34, fontWeight: 600, marginBottom: 4, color: '#1E1A15' },
	updated: { color: '#6B5F4D', marginTop: 0 },
	h2: { fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 600, marginTop: 36, color: '#1E1A15' },
	h3: { fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 600, marginTop: 22, color: '#1E1A15' },
	a: { color: '#D86641' },
	summary: {
		background: '#FBF0EB',
		borderLeft: '4px solid #D86641',
		borderRadius: 12,
		padding: '4px 20px',
		margin: '24px 0',
	},
};

const PrivacyPolicy = () => {
	return (
		<div style={styles.page}>
			<div style={styles.header}>
				<Link to="/">
					<img src={titleImg} alt="Italian Naturally" style={styles.logo} />
				</Link>
				<Link to="/" style={styles.back}>← Home</Link>
			</div>

			<div style={styles.container}>
				<h1 style={styles.h1}>Privacy Policy</h1>
				<p style={styles.updated}>Last updated: {LAST_UPDATED}</p>

				<p>
					Italian Naturally is a language-learning app and website (the
					&quot;Service&quot;) operated by Andrew Bogaard (&quot;we,&quot;
					&quot;us,&quot; or &quot;our&quot;). It teaches Italian through spoken
					conversations and gives you a pronunciation score when you practice out
					loud. This policy explains what we collect, why, and the choices you
					have. It applies to the Italian Naturally mobile app and to
					italiannaturally.com.
				</p>

				<div style={styles.summary}>
					<p><strong>The short version</strong></p>
					<ul>
						<li>We collect your email, an optional name, your learning progress, and your subscription status.</li>
						<li>When you practice speaking, the scoring happens <strong>on your device</strong> — your voice recordings are not sent to us and we never store them.</li>
						<li>Subscriptions are handled by the Apple App Store and Google Play. We never receive your payment card.</li>
						<li>We do not sell your personal information, and we don&apos;t use it for advertising or cross-app tracking.</li>
						<li>You can delete your account and data at any time — just email us.</li>
					</ul>
				</div>

				<h2 style={styles.h2}>Information We Collect</h2>

				<h3 style={styles.h3}>Information you give us</h3>
				<ul>
					<li>
						<strong>Account details:</strong> your email address and, optionally,
						your first and last name. Your password is stored only as a salted
						hash. If you sign in with Google, we receive your email, name, and
						Google account identifier to create and authenticate your account.
					</li>
					<li>
						<strong>Support messages:</strong> anything you email us when you ask
						for help.
					</li>
				</ul>

				<h3 style={styles.h3}>Information created as you use the Service</h3>
				<ul>
					<li>
						<strong>Voice input (processed on your device):</strong> to score
						your pronunciation, the app uses your microphone while you record a
						line and runs speech recognition <strong>locally on your
						device</strong>. The audio is processed in the moment to produce a
						score and recognized text; the <strong>recording itself is never
						uploaded to or stored by us</strong>. Only the resulting score and
						recognized text are saved to your learning progress.
					</li>
					<li>
						<strong>Learning progress:</strong> which conversations and lines
						you&apos;ve played, your pronunciation scores, and your
						spaced-repetition practice history, tied to your account.
					</li>
					<li>
						<strong>Subscription status:</strong> whether you have an active
						subscription and which product, so we can unlock the right content.
					</li>
					<li>
						<strong>Device &amp; technical data:</strong> limited data needed to
						run and secure the Service — for example device/OS type, app version,
						a subscription/install identifier, and error logs.
					</li>
				</ul>

				<h3 style={styles.h3}>What we do NOT collect</h3>
				<p>
					We do not receive your voice recordings, and we never collect your
					payment card details — purchases are handled entirely by Apple and
					Google. We do not use advertising identifiers or third-party
					advertising/tracking SDKs.
				</p>

				<h2 style={styles.h2}>How We Use Your Information</h2>
				<ul>
					<li>Provide, operate, and improve the Service.</li>
					<li>Create and secure your account and authenticate you.</li>
					<li>Score your pronunciation and track your learning progress.</li>
					<li>Activate the content your subscription unlocks.</li>
					<li>Send account/transactional emails (e.g., confirmation, password reset) and, unless you opt out, occasional product news.</li>
					<li>Respond to support requests.</li>
					<li>Detect and prevent fraud, abuse, and security issues, and meet legal obligations.</li>
				</ul>
				<p>
					For users in the EEA and UK, our legal bases are: performance of our
					contract with you (to provide the Service), your consent (microphone
					access and marketing email, which you can withdraw), our legitimate
					interests (to secure and improve the Service), and compliance with
					legal obligations.
				</p>

				<h2 style={styles.h2}>How We Share Information</h2>
				<p>
					We do <strong>not</strong> sell your personal information, and we do not
					&quot;share&quot; it for cross-context behavioral advertising (as those
					terms are used under U.S. state privacy laws). We disclose information
					only to:
				</p>
				<ul>
					<li>
						<strong>Service providers that work on our behalf,</strong> under
						contract and only as needed to run the Service:
						<ul>
							<li>Apple App Store and Google Play — process subscription payments;</li>
							<li>RevenueCat — manages subscription/entitlement status;</li>
							<li>Our email/CRM provider — sends account and (optional) marketing email;</li>
							<li>Our cloud hosting and logging providers — host the Service and store diagnostic logs.</li>
						</ul>
					</li>
					<li>
						<strong>Authorities,</strong> when we reasonably believe disclosure is
						required by law or to protect the rights, safety, or property of our
						users or others.
					</li>
					<li>
						<strong>A successor,</strong> in connection with a merger,
						acquisition, or sale of assets, subject to this policy.
					</li>
				</ul>

				<h2 style={styles.h2}>Your Rights and Choices</h2>
				<p>
					Depending on where you live, you may have the right to access, correct,
					delete, or receive a copy of your personal data, and to object to or
					restrict certain processing. California residents have the rights to
					know, delete, correct, and to not be discriminated against for
					exercising them; because we don&apos;t sell or share data for
					advertising, there&apos;s no opt-out needed for that.
				</p>
				<ul>
					<li>Update your account details in the app.</li>
					<li>Opt out of marketing email via the unsubscribe link in any such message.</li>
					<li>Manage or cancel your subscription in your Apple or Google account settings.</li>
					<li>
						<strong>Delete your account and data</strong> by emailing{' '}
						<a style={styles.a} href="mailto:abogaard.dev@gmail.com">abogaard.dev@gmail.com</a>.
						We&apos;ll delete or anonymize your data except where we must keep it
						to meet legal obligations.
					</li>
				</ul>
				<p>
					If you are in the EEA or UK, the data controller is Andrew Bogaard
					(contact details below), and you have the right to lodge a complaint
					with your local data-protection authority — in Italy, the Garante per
					la protezione dei dati personali (
					<a style={styles.a} href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">garanteprivacy.it</a>).
				</p>

				<h2 style={styles.h2}>Cookies and Local Storage</h2>
				<p>
					The mobile app uses your device&apos;s secure storage to keep you
					signed in. The website uses only essential storage needed for sign-in
					and basic functionality. We do not use advertising or analytics
					cookies.
				</p>

				<h2 style={styles.h2}>Data Retention</h2>
				<p>
					We keep your account and learning data while your account is active.
					After you delete your account, we delete or anonymize your personal
					data within a reasonable period, except where retention is required to
					comply with the law or resolve disputes.
				</p>

				<h2 style={styles.h2}>Children</h2>
				<p>
					The Service is not directed to children under 13 (or a higher minimum
					age where your country requires one — for example, 14 in Italy), and we
					do not knowingly collect personal information from them. If you believe
					a child has provided us personal information, contact us and we will
					delete it.
				</p>

				<h2 style={styles.h2}>International Transfers</h2>
				<p>
					We operate from Italy (in the EU). Your information is processed within
					the EU and may also be transferred to service providers located in the
					United States and elsewhere (for example, Apple, Google, and
					RevenueCat). Where we transfer personal data outside the EEA/UK, we rely
					on appropriate safeguards, such as the European Commission&apos;s
					Standard Contractual Clauses or an adequacy decision.
				</p>

				<h2 style={styles.h2}>Changes to This Policy</h2>
				<p>
					We may update this policy from time to time. We&apos;ll post the
					updated version here and change the &quot;Last updated&quot; date, and
					for material changes we&apos;ll provide additional notice (e.g., by
					email or in-app).
				</p>

				<h2 style={styles.h2}>Contact Us</h2>
				<p>
					Questions or requests? Contact us at{' '}
					<a style={styles.a} href="mailto:abogaard.dev@gmail.com">abogaard.dev@gmail.com</a>.
				</p>
				<p style={{ color: '#666' }}>
					Andrew Bogaard, Piuro (SO), Italy.
				</p>
			</div>
		</div>
	);
};

export default PrivacyPolicy;
